import React, { useMemo } from 'react';
import { FormDrawer } from './FormDrawer';
import {
  REQUEST_STATUS,
  getActiveRequestOrigins,
  getActiveRequestStatuses,
} from '../placeholders/planningDomain';
import {
  FORMULA_KIND_BASE,
  FORMULA_KIND_COLOR,
  PAINT_PRODUCT_OPTIONS,
} from '../placeholders/productionDomain';
import { useCommercialCatalogRefresh } from '../../hooks/useCommercialCatalogRefresh';

const emptyItem = () => ({
  product_code: '',
  product_name: '',
  color_code: '',
  color_name: '',
  formula_kind: FORMULA_KIND_COLOR,
  quantity: 1,
  unit: 'L',
});

export const emptyTcoatProductRequestForm = () => ({
  request_code: '',
  origin: 'Comercial',
  reference: '',
  needed_by_date: '',
  status: REQUEST_STATUS.requested,
  quotation_code: '',
  sale_code: '',
  notes: '',
  items: [emptyItem()],
});

export const TcoatProductRequestForm = ({
  isOpen,
  onClose,
  onSave,
  isSaving = false,
  isDarkMode = false,
  isEditing = false,
  formData,
  onChange,
  formError = '',
}) => {
  const catalogTick = useCommercialCatalogRefresh();
  const originOptions = useMemo(
    () => getActiveRequestOrigins().map((value) => ({ value, label: value })),
    [catalogTick]
  );
  const statusOptions = useMemo(
    () => getActiveRequestStatuses().map((value) => ({ value, label: value })),
    [catalogTick]
  );
  const items = Array.isArray(formData?.items) ? formData.items : [];

  const updateItem = (index, key, value) => {
    const next = items.map((item, idx) => {
      if (idx !== index) return item;
      const patch = { ...item, [key]: value };
      if (key === 'product_code') {
        const option = PAINT_PRODUCT_OPTIONS.find((row) => row.value === value);
        if (option) patch.product_name = option.label.split('·').slice(1).join('·').trim() || option.label;
      }
      return patch;
    });
    onChange('items', next);
  };

  const addItem = () => onChange('items', [...items, emptyItem()]);
  const removeItem = (index) => onChange('items', items.filter((_, idx) => idx !== index));

  const sections = [
    {
      key: 'basic',
      title: 'Datos de la solicitud',
      fields: [
        { key: 'request_code', label: 'Código', disabled: true, readOnly: true },
        {
          key: 'origin',
          label: 'Origen',
          type: 'select',
          options: originOptions,
        },
        { key: 'reference', label: 'Referencia / solicitante' },
        { key: 'needed_by_date', label: 'Fecha límite', type: 'date' },
        {
          key: 'status',
          label: 'Estado',
          type: 'select',
          options: statusOptions,
        },
        { key: 'quotation_code', label: 'Cotización (opcional)' },
        { key: 'sale_code', label: 'Venta (opcional)' },
        { key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true, rows: 2 },
      ],
    },
    {
      key: 'items',
      title: 'Productos T-COAT',
      fields: [
        {
          key: 'items',
          fullWidth: true,
          type: 'custom',
          render: ({ colors, baseInputStyle }) => (
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={addItem}
                  style={{
                    height: 30,
                    padding: '0 10px',
                    borderRadius: 8,
                    border: `1px solid ${colors.inputBorder}`,
                    background: 'transparent',
                    color: colors.text,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Agregar producto
                </button>
              </div>
              {items.length === 0 ? (
                <p style={{ margin: 0, fontSize: 12, color: colors.muted }}>Agrega al menos un producto.</p>
              ) : (
                items.map((item, index) => (
                  <div
                    key={`item-${index}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.2fr 1fr 0.8fr 0.8fr 0.6fr 0.5fr auto',
                      gap: 8,
                      padding: 10,
                      borderRadius: 8,
                      border: `1px solid ${colors.sectionBorder}`,
                      background: colors.sectionBg,
                      alignItems: 'end',
                    }}
                  >
                    <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Producto
                      <select
                        style={{ ...baseInputStyle, marginTop: 4 }}
                        value={item.product_code || ''}
                        onChange={(e) => updateItem(index, 'product_code', e.target.value)}
                      >
                        <option value="">Seleccionar…</option>
                        {PAINT_PRODUCT_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Nombre
                      <input
                        style={{ ...baseInputStyle, marginTop: 4 }}
                        value={item.product_name || ''}
                        onChange={(e) => updateItem(index, 'product_name', e.target.value)}
                      />
                    </label>
                    <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Cód. color
                      <input
                        style={{ ...baseInputStyle, marginTop: 4 }}
                        value={item.color_code || ''}
                        onChange={(e) => updateItem(index, 'color_code', e.target.value)}
                      />
                    </label>
                    <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Color
                      <input
                        style={{ ...baseInputStyle, marginTop: 4 }}
                        value={item.color_name || ''}
                        onChange={(e) => updateItem(index, 'color_name', e.target.value)}
                      />
                    </label>
                    <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Cant.
                      <input
                        type="number"
                        step="0.01"
                        style={{ ...baseInputStyle, marginTop: 4 }}
                        value={item.quantity ?? 1}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      />
                    </label>
                    <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Unidad
                      <input
                        style={{ ...baseInputStyle, marginTop: 4 }}
                        value={item.unit || 'L'}
                        onChange={(e) => updateItem(index, 'unit', e.target.value)}
                      />
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <select
                        style={{ ...baseInputStyle, fontSize: 11 }}
                        value={item.formula_kind || FORMULA_KIND_COLOR}
                        onChange={(e) => updateItem(index, 'formula_kind', e.target.value)}
                      >
                        <option value={FORMULA_KIND_COLOR}>Color</option>
                        <option value={FORMULA_KIND_BASE}>Base</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        style={{
                          height: 34,
                          padding: '0 10px',
                          borderRadius: 8,
                          border: `1px solid ${colors.inputBorder}`,
                          background: 'transparent',
                          color: '#b91c1c',
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        Quitar
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          ),
        },
      ],
    },
  ];

  return (
    <FormDrawer
      isOpen={isOpen}
      onClose={onClose}
      onSave={onSave}
      isSaving={isSaving}
      isDarkMode={isDarkMode}
      title={isEditing ? 'Editar solicitud T-COAT' : 'Nueva solicitud T-COAT'}
      subtitle="Comercial · Producción"
      sections={sections}
      formData={formData}
      onChange={onChange}
      formError={formError}
      width={820}
    />
  );
};

export default TcoatProductRequestForm;
